/**
 * Communication Board Default Content
 * 
 * New structure: Main grid with direct utterances (white) and expandable categories (yellow)
 * Yellow items open inner screens, white items speak directly
 * 
 * Color coding (AAC standards):
 * - purple: pronouns (כינויים)
 * - teal: responses/affirmations (תגובות)
 * - gray: connectors (מילות קישור)
 * - green: verbs that expand to inner screens (פעלים - מרחיבים)
 * - blue: questions category
 * - yellow: topic categories
 * - orange: needs/descriptors
 * - pink: feelings
 */

import type { BoardCell, CategoryCell, UtteranceCell, CommunicationBoardData } from "./communicationBoardTypes";

// Type for items that can be either direct utterance or expandable category
export type MainBoardItemType = "direct" | "category";

export interface MainBoardItem {
  id: string;
  type: MainBoardItemType;
  // For direct items
  text?: Record<string, string>; // { he: "אני", en: "I", ru: "Я", ar: "أنا" }
  // For category items
  name?: Record<string, string>; // Category display name
  nameFemale?: Record<string, string>; // Feminine form of category name (Hebrew only)
  innerItems?: Record<string, string[]>; // Inner screen items per language
  innerItemsFemale?: Record<string, string[]>; // Feminine forms for inner items (Hebrew only)
  innerEmojis?: string[]; // Emojis for inner items (optional, mainly for green categories)
  color: string;
  emoji?: string;
}

// ========== MAIN BOARD LAYOUT ==========
// Based on user specification - 5 rows with mixed direct utterances and categories

export const mainBoardLayout: MainBoardItem[][] = [
  // Row 1: Pronouns (all direct - purple) - אני, אתה, את, הוא, היא, הם
  [
    { id: "pronoun-i", type: "direct", text: { he: "אני", en: "I", ru: "я", ar: "أنا" }, color: "purple" },
    { id: "pronoun-you-m", type: "direct", text: { he: "אתה", en: "you", ru: "ты", ar: "أنتَ" }, color: "purple" },
    { id: "pronoun-you-f", type: "direct", text: { he: "את", en: "we", ru: "ты", ar: "أنتِ" }, color: "purple" },
    { id: "pronoun-he", type: "direct", text: { he: "הוא", en: "he", ru: "он", ar: "هو" }, color: "purple" },
    { id: "pronoun-she", type: "direct", text: { he: "היא", en: "she", ru: "она", ar: "هي" }, color: "purple" },
    { id: "pronoun-they", type: "direct", text: { he: "הם", en: "they", ru: "они", ar: "هم" }, color: "purple" },
  ],
  // Row 2: Basic responses (all direct - teal) - לא, כן, בבקשה, תודה, מספיק, עוד
  [
    { id: "resp-no", type: "direct", text: { he: "לא", en: "no", ru: "нет", ar: "لا" }, color: "teal" },
    { id: "resp-yes", type: "direct", text: { he: "כן", en: "yes", ru: "да", ar: "نعم" }, color: "teal" },
    { id: "resp-please", type: "direct", text: { he: "בבקשה", en: "please", ru: "пожалуйста", ar: "من فضلك" }, color: "teal" },
    { id: "resp-thanks", type: "direct", text: { he: "תודה", en: "thank you", ru: "спасибо", ar: "شكراً" }, color: "teal" },
    { id: "resp-enough", type: "direct", text: { he: "מספיק", en: "enough", ru: "хватит", ar: "كافي" }, color: "teal" },
    { id: "resp-more", type: "direct", text: { he: "עוד", en: "more", ru: "ещё", ar: "المزيد" }, color: "teal" },
  ],
  // Row 3: Verbs that expand (all categories - green) - רוצה, צריך, מרגיש, חושב, אוהב, יודע
  [
    { 
      id: "cat-want", 
      type: "category", 
      name: { he: "רוצה", en: "want", ru: "хочу", ar: "أريد" },
      color: "green",
      innerItems: {
        // Row 1: Basic needs
        // Row 2: Physical comfort  
        // Row 3: Rest & entertainment
        // Row 4: Social & movement
        // Row 5: Communication & learning
        he: [
          "לאכול", "לשתות", "סאקשן", "שירותים", "מקלחת", "אור",
          "לשנות תנוחה", "משכך כאבים", "מזגן", "שמיכה", "כרית", "לגרד",
          "לנוח", "לראות טלויזיה", "לשמוע מוזיקה", "שקט", "חושך", "לישון",
          "להפגש", "לצאת החוצה", "להיות לבד", "לחזור הביתה", "לטייל", "לנסוע",
          "לשמוע על", "לעצור", "להמשיך", "להגיד", "לעשות", "ללמוד"
        ],
        en: [
          "to eat", "to drink", "suction", "bathroom", "shower", "light",
          "change position", "painkiller", "air conditioner", "blanket", "pillow", "to scratch",
          "to rest", "watch TV", "listen to music", "quiet", "darkness", "to sleep",
          "to meet", "go outside", "be alone", "go home", "to walk", "to travel",
          "to hear about", "to stop", "to continue", "to say", "to do", "to learn"
        ],
        ru: [
          "есть", "пить", "аспирацию", "туалет", "душ", "свет",
          "сменить позу", "обезболивающее", "кондиционер", "одеяло", "подушку", "почесать",
          "отдохнуть", "смотреть ТВ", "слушать музыку", "тишину", "темноту", "спать",
          "встретиться", "выйти на улицу", "побыть одному", "домой", "гулять", "поехать",
          "услышать о", "остановиться", "продолжить", "сказать", "сделать", "учиться"
        ],
        ar: [
          "آكل", "أشرب", "شفط", "حمام", "دش", "ضوء",
          "تغيير الوضعية", "مسكن ألم", "مكيف", "بطانية", "وسادة", "أن أحك",
          "أستريح", "أشاهد التلفزيون", "أسمع موسيقى", "هدوء", "ظلام", "أنام",
          "ألتقي", "أخرج للخارج", "أكون وحدي", "أعود للبيت", "أتمشى", "أسافر",
          "أسمع عن", "أتوقف", "أستمر", "أقول", "أفعل", "أتعلم"
        ],
      },
      innerEmojis: [
        "🍽️", "🥤", "💨", "🚽", "🚿", "💡",
        "🔄", "💊", "❄️", "🛏️", "☁️", "✋",
        "😌", "📺", "🎵", "🤫", "🌙", "💤",
        "🤝", "🌳", "🧘", "🏠", "🚶", "🚗",
        "👂", "✋", "▶️", "💬", "✨", "📚"
      ]
    },
    { 
      id: "cat-need", 
      type: "category", 
      name: { he: "צריך", en: "need", ru: "нужно", ar: "أحتاج" },
      nameFemale: { he: "צריכה" },
      color: "green",
      innerItems: {
        // Row 1: Urgent/medical needs
        // Row 2: Comfort/emotional needs
        // Row 3: Basic needs/items
        // Row 4: Environment/personal
        he: [
          "עזרה", "שינוי תנוחה", "משכך כאבים", "סאקשן", "לראות רופא", "מזגן",
          "שירותים", "מנוחה", "שקט", "להבין משהו", "להיות לבד", "מישהו לידי",
          "לשתות", "לאכול", "לצאת החוצה", "משקפיים", "מכשיר שמיעה", "תרופה",
          "חושך", "אור", "שמיכה", "כרית", "לגרד", "פרטיות"
        ],
        en: [
          "help", "change position", "painkiller", "suction", "see doctor", "air conditioner",
          "bathroom", "rest", "quiet", "understand something", "be alone", "someone nearby",
          "drink", "eat", "go outside", "glasses", "hearing aid", "medicine",
          "darkness", "light", "blanket", "pillow", "scratch", "privacy"
        ],
        ru: [
          "помощь", "сменить позу", "обезболивающее", "аспирацию", "к врачу", "кондиционер",
          "туалет", "отдых", "тишину", "понять что-то", "побыть одному", "кого-то рядом",
          "пить", "есть", "выйти", "очки", "слуховой аппарат", "лекарство",
          "темноту", "свет", "одеяло", "подушку", "почесать", "уединение"
        ],
        ar: [
          "مساعدة", "تغيير الوضعية", "مسكن ألم", "شفط", "رؤية طبيب", "مكيف",
          "حمام", "راحة", "هدوء", "فهم شيء", "أن أكون وحدي", "شخص بجانبي",
          "شرب", "أكل", "الخروج", "نظارات", "سماعة أذن", "دواء",
          "ظلام", "ضوء", "بطانية", "وسادة", "أن أحك", "خصوصية"
        ],
      },
      innerEmojis: [
        "🆘", "🔄", "💊", "💨", "👨‍⚕️", "❄️",
        "🚽", "😌", "🤫", "🤔", "🧘", "👥",
        "🥤", "🍽️", "🌳", "👓", "🦻", "💉",
        "🌙", "💡", "🛏️", "☁️", "✋", "🚪"
      ]
    },
    { 
      id: "cat-feel", 
      type: "category", 
      name: { he: "מרגיש", en: "feel", ru: "чувствую", ar: "أشعر" },
      nameFemale: { he: "מרגישה" },
      color: "green",
      innerItems: {
        // Row 1: Basic physical state
        // Row 2: Physical sensations
        // Row 3: Positive emotions
        // Row 4: Complex emotions
        // Row 5: Challenging emotions
        he: [
          "טוב", "לא טוב", "כאוב", "סחרחורת", "עייף", "בחילה",
          "חולשה", "קר לי", "חם לי", "לחץ בחזה", "מגרד לי", "לא נוח לי",
          "שמחה", "רוגע", "אופטימיות", "חיוניות", "נעים", "בסדר",
          "התרגשות", "לחץ", "ציפייה", "חוסר סבלנות", "חוסר אונים", "עצב",
          "תסכול", "קושי להתרכז", "דאגה", "פחד", "בדידות", "בלבול"
        ],
        en: [
          "good", "not good", "pain", "dizziness", "tired", "nausea",
          "weakness", "I'm cold", "I'm hot", "chest pressure", "itchy", "uncomfortable",
          "happiness", "calm", "optimism", "vitality", "pleasant", "okay",
          "excitement", "pressure", "anticipation", "impatience", "helplessness", "sadness",
          "frustration", "difficulty concentrating", "worry", "fear", "loneliness", "confusion"
        ],
        ru: [
          "хорошо", "плохо", "больно", "головокружение", "устал", "тошнота",
          "слабость", "мне холодно", "мне жарко", "давит в груди", "чешется", "неудобно",
          "счастье", "спокойствие", "оптимизм", "бодрость", "приятно", "нормально",
          "волнение", "давление", "ожидание", "нетерпение", "беспомощность", "грусть",
          "разочарование", "трудно сосредоточиться", "беспокойство", "страх", "одиночество", "смятение"
        ],
        ar: [
          "جيد", "ليس جيد", "ألم", "دوار", "متعب", "غثيان",
          "ضعف", "أشعر بالبرد", "أشعر بالحر", "ضغط في الصدر", "حكة", "غير مرتاح",
          "سعادة", "هدوء", "تفاؤل", "حيوية", "مريح", "بخير",
          "إثارة", "ضغط", "توقع", "نفاد صبر", "عجز", "حزن",
          "إحباط", "صعوبة التركيز", "قلق", "خوف", "وحدة", "ارتباك"
        ],
      },
      innerEmojis: [
        "😊", "😔", "🤕", "🫨", "😴", "🤢",
        "😩", "🥶", "🥵", "💔", "🤚", "😣",
        "😃", "😌", "🌈", "⚡", "😊", "👍",
        "🎉", "😰", "⏳", "😤", "😞", "😢",
        "😤", "🧠", "😟", "😨", "🧍", "😕"
      ]
    },
    { 
      id: "cat-think", 
      type: "category", 
      name: { he: "חושב", en: "think", ru: "думаю", ar: "أعتقد" },
      nameFemale: { he: "חושבת" },
      color: "green",
      innerItems: {
        he: [
          "שזה טוב", "שזה לא טוב", "שזה רע מאוד", "עליך", "שזה רעיון טוב", "שזה רעיון לא טוב",
          "שכן", "שלא", "שאולי", "שזה מעניין", "שזה לא מעניין", "שאפשר אחרת"
        ],
        en: [
          "it's good", "it's not good", "it's very bad", "about you", "good idea", "bad idea",
          "that yes", "that no", "maybe", "it's interesting", "not interesting", "there's another way"
        ],
        ru: [
          "это хорошо", "это нехорошо", "это очень плохо", "о тебе", "хорошая идея", "плохая идея",
          "что да", "что нет", "может быть", "это интересно", "не интересно", "можно иначе"
        ],
        ar: [
          "هذا جيد", "هذا ليس جيد", "هذا سيء جداً", "عنك", "فكرة جيدة", "فكرة سيئة",
          "نعم", "لا", "ربما", "هذا مثير", "ليس مثير", "يمكن بطريقة أخرى"
        ],
      },
      innerEmojis: [
        "👍", "👎", "😰", "👤", "💡", "🚫",
        "✅", "❌", "🤷", "🤩", "😐", "🔄"
      ]
    },
    { 
      id: "cat-love", 
      type: "category", 
      name: { he: "אוהב", en: "love", ru: "люблю", ar: "أحب" },
      nameFemale: { he: "אוהבת" },
      color: "green",
      innerItems: {
        he: [
          "אותך", "אותו", "אותה", "אתכם", "את המשפחה", "את החברים",
          "להיות איתך", "להיות איתכם", "ככה", "את זה", "מאוד", "לפעמים",
          "להיות בחוץ", "להיות עם אנשים", "ללמוד", "לצפות בטלויזיה", "לשמוע מוזיקה", "לצחוק",
          "לראות", "לשמוע", "לעשות", "לאכול"
        ],
        en: [
          "you", "him", "her", "you all", "the family", "the friends",
          "to be with you", "to be with you all", "like this", "this", "very much", "sometimes",
          "to be outside", "to be with people", "to learn", "to watch TV", "to listen to music", "to laugh",
          "to see", "to hear", "to do", "to eat"
        ],
        ru: [
          "тебя", "его", "её", "вас", "семью", "друзей",
          "быть с тобой", "быть с вами", "так", "это", "очень", "иногда",
          "быть на улице", "быть с людьми", "учиться", "смотреть ТВ", "слушать музыку", "смеяться",
          "видеть", "слышать", "делать", "есть"
        ],
        ar: [
          "إياك", "إياه", "إياها", "إياكم", "العائلة", "الأصدقاء",
          "أن أكون معك", "أن أكون معكم", "هكذا", "هذا", "كثيراً", "أحياناً",
          "أن أكون في الخارج", "أن أكون مع الناس", "أن أتعلم", "مشاهدة التلفزيون", "الاستماع للموسيقى", "الضحك",
          "أن أرى", "أن أسمع", "أن أفعل", "أن آكل"
        ],
      },
      innerEmojis: [
        "❤️", "💙", "💜", "💕", "👨‍👩‍👧‍👦", "👥",
        "🤗", "🫂", "👌", "👆", "💯", "🕐",
        "🌳", "👫", "📚", "📺", "🎵", "😂",
        "👀", "👂", "✨", "🍽️"
      ]
    },
    { 
      id: "cat-dont-like", 
      type: "category", 
      name: { he: "לא אוהב", en: "don't like", ru: "не люблю", ar: "لا أحب" },
      nameFemale: { he: "לא אוהבת" },
      color: "green",
      innerItems: {
        he: [
          "את זה", "להרגיש ככה", "להיות לבד", "להיות כאן", "את הסיטואציה", "לחכות",
          "את הריח", "את הרעש", "את המגע", "אור חזק", "רעש חזק", "הרבה אנשים"
        ],
        en: [
          "this", "feeling this way", "being alone", "being here", "the situation", "waiting",
          "the smell", "the noise", "the touch", "bright light", "loud noise", "many people"
        ],
        ru: [
          "это", "чувствовать так", "быть одному", "быть здесь", "ситуацию", "ждать",
          "запах", "шум", "прикосновение", "яркий свет", "громкий шум", "много людей"
        ],
        ar: [
          "هذا", "الشعور هكذا", "أن أكون وحدي", "أن أكون هنا", "الوضع", "الانتظار",
          "الرائحة", "الضوضاء", "اللمس", "الضوء الساطع", "الضوضاء العالية", "الكثير من الناس"
        ],
      },
      innerEmojis: [
        "👎", "😔", "🧍", "📍", "⚠️", "⏳",
        "👃", "🔊", "✋", "💡", "📢", "👥"
      ]
    },
  ],
  // Row 4: Connectors (all direct - gray) - את, זה, ש, עם, ו-, אם
  [
    { id: "conn-et", type: "direct", text: { he: "את", en: "the", ru: "ну", ar: "هو" }, color: "gray" },
    { id: "conn-this", type: "direct", text: { he: "זה", en: "this", ru: "это", ar: "هذا" }, color: "gray" },
    { id: "conn-that", type: "direct", text: { he: "ש", en: "that", ru: "что", ar: "أن" }, color: "gray" },
    { id: "conn-with", type: "direct", text: { he: "עם", en: "with", ru: "с", ar: "مع" }, color: "gray" },
    { id: "conn-and", type: "direct", text: { he: "ו-", en: "and", ru: "и", ar: "و" }, color: "gray" },
    { id: "conn-if", type: "direct", text: { he: "אם", en: "if", ru: "если", ar: "إذا" }, color: "gray" },
  ],
  // Row 5: Topic categories (all expandable - yellow)
  [
    { 
      id: "cat-questions", 
      type: "category", 
      name: { he: "שאלות", en: "questions", ru: "вопросы", ar: "أسئلة" },
      color: "yellow",
      innerItems: {
        he: ["מתי הולכים לשם\u200F?", "מתי המטפל יבוא\u200F?", "מה הרופא אמר\u200F?", "מה קרה\u200F?", "איפה הוא\u200F?", "איפה היא\u200F?", "מה עומד לקרות\u200F?", "למה זה ככה\u200F?", "יש אופציה אחרת\u200F?"],
        en: ["when are we going there?", "when will the caregiver come?", "what did the doctor say?", "what happened?", "where is he?", "where is she?", "what is going to happen?", "why is it like this?", "is there another option?"],
        ru: ["когда мы туда пойдём?", "когда придёт сиделка?", "что сказал врач?", "что случилось?", "где он?", "где она?", "что будет?", "почему так?", "есть другой вариант?"],
        ar: ["متى نذهب إلى هناك؟", "متى سيأتي مقدم الرعاية؟", "ماذا قال الطبيب؟", "ماذا حدث؟", "أين هو؟", "أين هي؟", "ما الذي سيحدث؟", "لماذا هكذا؟", "هل يوجد خيار آخر؟"],
      }
    },
    { 
      id: "cat-meeting", 
      type: "category", 
      name: { he: "פגישה", en: "meeting", ru: "встреча", ar: "لقاء" },
      color: "yellow",
      innerItems: {
        he: ["לא", "כן", "בבקשה", "תודה", "שלום", "מה שלומך\u200F?", "כייף לראות אותך", "נעים מאוד להכיר", "איזה יופי", "אני שמח לשמוע", "כל הכבוד", "איזו הפתעה", "ספר לי עוד על זה", "לא הבנתי", "אשמח שתבוא שוב", "להתראות", "אני עייף", "סליחה", "תוכל לחזור\u200F?", "מה קרה\u200F?", "אוי ואבוי", "לא חשבתי על זה"],
        en: ["no", "yes", "please", "thank you", "hello", "how are you?", "nice to see you", "nice to meet you", "how wonderful", "I'm happy to hear", "well done", "what a surprise", "tell me more about it", "I didn't understand", "I'd love you to come again", "goodbye", "I'm tired", "sorry", "can you repeat?", "what happened?", "oh no", "I didn't think of that"],
        ru: ["нет", "да", "пожалуйста", "спасибо", "привет", "как дела?", "рад тебя видеть", "очень приятно познакомиться", "как здорово", "рад слышать", "молодец", "какой сюрприз", "расскажи ещё об этом", "я не понял", "буду рад если придёшь снова", "до свидания", "я устал", "извини", "можешь повторить?", "что случилось?", "ой-ой", "я не подумал об этом"],
        ar: ["لا", "نعم", "من فضلك", "شكراً", "مرحباً", "كيف حالك؟", "سعيد برؤيتك", "تشرفنا بمعرفتك", "رائع", "سعيد لسماع ذلك", "أحسنت", "يا للمفاجأة", "أخبرني المزيد", "لم أفهم", "سأكون سعيداً لو تأتي مجدداً", "مع السلامة", "أنا متعب", "آسف", "هل يمكنك الإعادة؟", "ماذا حدث؟", "يا للأسف", "لم أفكر في ذلك"],
      },
      innerItemsFemale: {
        he: ["לא", "כן", "בבקשה", "תודה", "שלום", "מה שלומך\u200F?", "כייף לראות אותך", "נעים מאוד להכיר", "איזה יופי", "אני שמחה לשמוע", "כל הכבוד", "איזו הפתעה", "ספר לי עוד על זה", "לא הבנתי", "אשמח שתבוא שוב", "להתראות", "אני עייפה", "סליחה", "תוכל לחזור\u200F?", "מה קרה\u200F?", "אוי ואבוי", "לא חשבתי על זה"],
      }
    },
    { 
      id: "cat-sleep", 
      type: "category", 
      name: { he: "שינה", en: "sleep", ru: "сон", ar: "النوم" },
      color: "yellow",
      innerItems: {
        he: ["לא", "כן", "בבקשה", "תודה", "אני רוצה לישון", "אני עייף מאוד", "רק ללכת לנוח", "להחליף לפיג'מה", "בלי להתקלח", "קודם להתקלח", "צריך לצחצח שיניים", "רוצה לשתות", "לא נוח לי", "קר לי", "חם לי", "מגרד לי", "ככה זה טוב", "נעים לי", "לכבות את האור", "להדליק את האור", "לשמוע מוזיקה", "לראות טלויזיה"],
        en: ["no", "yes", "please", "thank you", "I want to sleep", "I'm very tired", "just rest", "change to pajamas", "without showering", "shower first", "I need to brush teeth", "I want to drink", "I'm not comfortable", "I'm cold", "I'm hot", "it's itchy", "this is good", "I'm comfortable", "turn off the light", "turn on the light", "listen to music", "watch TV"],
        ru: ["нет", "да", "пожалуйста", "спасибо", "хочу спать", "очень устал", "просто отдохнуть", "переодеться в пижаму", "без душа", "сначала в душ", "нужно почистить зубы", "хочу пить", "мне неудобно", "мне холодно", "мне жарко", "чешется", "так хорошо", "мне удобно", "выключить свет", "включить свет", "слушать музыку", "смотреть ТВ"],
        ar: ["لا", "نعم", "من فضلك", "شكراً", "أريد أن أنام", "أنا متعب جداً", "فقط أستريح", "أغير إلى البيجاما", "بدون استحمام", "الاستحمام أولاً", "أحتاج تنظيف الأسنان", "أريد أن أشرب", "لست مرتاحاً", "أشعر بالبرد", "أشعر بالحر", "يحكني", "هكذا جيد", "أنا مرتاح", "إطفاء الضوء", "إشعال الضوء", "الاستماع للموسيقى", "مشاهدة التلفزيون"],
      }
    },
    { 
      id: "cat-meal", 
      type: "category", 
      name: { he: "ארוחה", en: "meal", ru: "еда", ar: "وجبة" },
      color: "yellow",
      innerItems: {
        he: ["לא", "כן", "בבקשה", "תודה", "אני רעב", "רוצה את זה", "עוד", "מספיק", "צריך לנקות", "אני רוצה לשתות", "צריך מלח", "חריף לי", "לא טעים לי", "סיימתי", "תודה", "היה מעולה", "אני מרגיש לא טוב", "אמשיך אחר כך"],
        en: ["no", "yes", "please", "thank you", "I'm hungry", "I want this", "more", "enough", "need to clean", "I want to drink", "need salt", "too spicy", "doesn't taste good", "I'm done", "thank you", "it was great", "I don't feel well", "I'll continue later"],
        ru: ["нет", "да", "пожалуйста", "спасибо", "я голоден", "хочу это", "ещё", "достаточно", "нужно убрать", "хочу пить", "нужна соль", "слишком острое", "невкусно", "я закончил", "спасибо", "было отлично", "мне плохо", "продолжу позже"],
        ar: ["لا", "نعم", "من فضلك", "شكراً", "أنا جائع", "أريد هذا", "المزيد", "كافي", "يحتاج تنظيف", "أريد أن أشرب", "أحتاج ملح", "حار جداً", "لا طعم له", "انتهيت", "شكراً", "كان رائعاً", "لا أشعر بخير", "سأكمل لاحقاً"],
      },
      // Feminine versions for Hebrew - only items that change
      innerItemsFemale: {
        he: ["לא", "כן", "בבקשה", "תודה", "אני רעבה", "רוצה את זה", "עוד", "מספיק", "צריך לנקות", "אני רוצה לשתות", "צריך מלח", "חריף לי", "לא טעים לי", "סיימתי", "תודה", "היה מעולה", "אני מרגישה לא טוב", "אמשיך אחר כך"],
      }
    },
    { 
      id: "cat-outside", 
      type: "category", 
      name: { he: "בחוץ", en: "outside", ru: "на улице", ar: "في الخارج" },
      color: "yellow",
      innerItems: {
        he: ["לא", "כן", "בבקשה", "תודה", "יש כאן יותר מדי רעש", "חם לי", "קר לי", "נעים לי", "לאן הולכים\u200F?", "מתי חוזרים\u200F?", "מה זה\u200F?", "רוצה לעצור כאן", "רוצה ללכת למקום אחר", "זה מפריע לי", "לאט יותר", "מהר יותר", "אפשר לחזור\u200F?", "לא נוח לי", "לגינה", "לפארק", "לבקר מישהו", "לקניון", "לים", "לטייל ברחוב"],
        en: ["no", "yes", "please", "thank you", "too much noise here", "I'm hot", "I'm cold", "I'm comfortable", "where are we going?", "when are we coming back?", "what is this?", "want to stop here", "want to go somewhere else", "this bothers me", "slower", "faster", "can we go back?", "I'm not comfortable", "to the garden", "to the park", "to visit someone", "to the mall", "to the beach", "walk on the street"],
        ru: ["нет", "да", "пожалуйста", "спасибо", "здесь слишком шумно", "мне жарко", "мне холодно", "мне хорошо", "куда идём?", "когда вернёмся?", "что это?", "хочу остановиться здесь", "хочу в другое место", "это мне мешает", "медленнее", "быстрее", "можно вернуться?", "мне неудобно", "в сад", "в парк", "навестить кого-то", "в торговый центр", "на море", "гулять по улице"],
        ar: ["لا", "نعم", "من فضلك", "شكراً", "ضجيج كثير هنا", "حار لي", "بارد لي", "مرتاح", "إلى أين نذهب؟", "متى نعود؟", "ما هذا؟", "أريد التوقف هنا", "أريد الذهاب لمكان آخر", "هذا يزعجني", "أبطأ", "أسرع", "هل يمكننا العودة؟", "لست مرتاحاً", "للحديقة", "للمنتزه", "لزيارة شخص", "للمول", "للبحر", "للمشي في الشارع"],
      },
      // No gender-specific text for this category - "רוצה" needs TTS phonetic override, not visual text change
    },
    { 
      id: "cat-phrases", 
      type: "category", 
      name: { he: "משפטים", en: "phrases", ru: "фразы", ar: "عبارات" },
      color: "yellow",
      innerItems: {
        he: [
          "אני בסדר", "בוא אליי", "צריך עזרה", "תתקשר אליי", "אני אוהב אותך", "תודה",
          "מתי תבוא?", "אני מחכה", "הכל טוב", "אני צריך משהו", "אני עייף", "אני רעב"
        ],
        en: [
          "I'm okay", "Come to me", "Need help", "Call me", "I love you", "Thank you",
          "When are you coming?", "I'm waiting", "Everything is fine", "I need something", "I'm tired", "I'm hungry"
        ],
        ru: [
          "я в порядке", "подойди ко мне", "нужна помощь", "позвони мне", "я тебя люблю", "спасибо",
          "когда придёшь?", "я жду", "всё хорошо", "мне нужно кое-что", "я устал", "я голоден"
        ],
        ar: [
          "أنا بخير", "تعال إلي", "أحتاج مساعدة", "اتصل بي", "أحبك", "شكراً",
          "متى ستأتي؟", "أنا أنتظر", "كل شيء على ما يرام", "أحتاج شيئاً", "أنا متعب", "أنا جائع"
        ],
      },
      innerItemsFemale: {
        he: [
          "אני בסדר", "בוא אליי", "צריכה עזרה", "תתקשר אליי", "אני אוהבת אותך", "תודה",
          "מתי תבוא?", "אני מחכה", "הכל טוב", "אני צריכה משהו", "אני עייפה", "אני רעבה"
        ],
      }
    },
  ],
];

// Convert the layout to the CommunicationBoardData format
export function getNewDefaultBoard(language: "he" | "en" | "ru" | "ar" = "he", gender: "male" | "female" | "neutral" = "neutral"): CommunicationBoardData {
  const lang = language as string;
  const isFemale = gender === "female" && lang === "he"; // Gender only applies to Hebrew
  
  const cells: BoardCell[][] = mainBoardLayout.map((row, rowIndex) =>
    row.map((item, colIndex) => {
      if (!item) return null;
      
      if (item.type === "direct") {
        // Direct utterance - speaks immediately
        const utterance: UtteranceCell = {
          id: item.id,
          type: "utterance",
          text: item.text?.[lang] || item.text?.he || "",
          color: item.color,
          emoji: item.emoji,
        };
        return utterance;
      } else {
        // Category - opens inner screen
        // Use feminine inner items if available for Hebrew female gender
        const innerItems = (isFemale && item.innerItemsFemale?.he) 
          ? item.innerItemsFemale.he 
          : (item.innerItems?.[lang] || item.innerItems?.he || []);
        const innerEmojis = item.innerEmojis || [];
        const innerUtterances: UtteranceCell[] = innerItems.map((text, i) => ({
          id: `${item.id}-item-${i}`,
          type: "utterance" as const,
          text,
          color: item.color,
          emoji: innerEmojis[i] || undefined,
        }));
        
        // Use feminine category name if available for Hebrew female gender
        const categoryName = (isFemale && item.nameFemale?.he) 
          ? item.nameFemale.he 
          : (item.name?.[lang] || item.name?.he || "");
        
        const category: CategoryCell = {
          id: item.id,
          type: "category",
          name: categoryName,
          color: item.color,
          emoji: item.emoji,
          utterances: innerUtterances,
        };
        return category;
      }
    })
  );
  
  const boardNames: Record<string, string> = {
    he: "לוח תקשורת",
    en: "Communication Board",
    ru: "Доска общения",
    ar: "لوحة التواصل",
  };
  
  return {
    id: "default",
    name: boardNames[lang] || boardNames.he,
    icon: "MessageCircle",
    rows: cells.length,
    columns: 6,
    cells,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

// Get supported languages list
export const supportedLanguages = ["he", "en", "ru", "ar"] as const;
export type SupportedLanguage = typeof supportedLanguages[number];
